// The connection to the server: a WebSocket whose messages are the protocol's (protocol.ts), read and
// written by the schemas the server hands out (binary.ts). It starts with the server's challenge, which
// the page signs with the account's key (identity.ts); then the requests go — the meeting the user is
// in or asked for first. A connection that goes is made again, waiting longer each time (up to 5 s).
//
// A server of a new version may speak another protocol than the one the page loaded the schemas of: before
// each connection made again — and when a message does not read — the page asks the server's schemas, and
// if they are not the same it loads anew, its code and the schemas together, sending nothing in the old
// ones.
import { codec, type Schema } from "./binary.ts";
import { hangup } from "./call.ts";
import { helloText } from "./identity.ts";
import { onServer } from "./meeting.ts";
import type { ClientMsg, ServerMsg } from "./protocol.ts";
import { MEETING_TITLE, RETRY, changed, show, state } from "./store.ts";

const MOST_RETRY = 5000;

/** The server's schemas as the page loaded them. */
let schemas = "";

/** The codecs of the protocol's messages, by the server's schemas. */
export async function loadSchemas(): Promise<void> {
  const r = await fetch("/schema.json", { cache: "no-store" });
  schemas = await r.text();
  const s = JSON.parse(schemas) as { client: Schema; server: Schema };
  state.codecs = { client: codec<ClientMsg>(s.client), server: codec<ServerMsg>(s.server) };
}

/** Whether the server's schemas are still those the page loaded; yes when they cannot be asked now (the
 * connection itself will tell). */
async function unchanged(): Promise<boolean> {
  try {
    const r = await fetch("/schema.json", { cache: "no-store" });
    return !r.ok || (await r.text()) === schemas;
  } catch {
    return true;
  }
}

/** The page loaded anew if the server's schemas changed: a new version of the server came. */
async function current(): Promise<boolean> {
  if (await unchanged()) return true;
  location.reload();
  return false;
}

export function connect(): void {
  const ws = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`);
  ws.binaryType = "arraybuffer";
  state.ws = ws;
  ws.onmessage = (e) => {
    let m: ServerMsg;
    try {
      m = state.codecs!.server.decode(new Uint8Array(e.data as ArrayBuffer));
    } catch (err) {
      console.error("a message the client cannot read:", err);
      void current();
      return;
    }
    if (m.type === "challenge") void signIn(ws, m.nonce);
    else onServer(m);
  };
  ws.onclose = () => {
    if (state.ws !== ws) return;
    state.up = false;
    state.asking = false;
    // The server's side of the call went with the connection: it starts again after it.
    hangup(false);
    show("нет связи с сервером, соединяюсь…", true);
    setTimeout(() => void again(), state.retry);
    state.retry = Math.min(state.retry * 2, MOST_RETRY);
  };
}

/** The connection made again, if the server speaks the page's protocol still. */
async function again(): Promise<void> {
  if (await current()) connect();
}

/** The connection made again (as another account, say). */
export function reconnect(): void {
  const ws = state.ws;
  state.ws = null;
  state.up = false;
  ws?.close();
  connect();
}

/** A request to the server, if the page is signed in. */
export function send(m: ClientMsg): void {
  if (state.up && state.ws !== null) state.ws.send(state.codecs!.client.encode(m));
}

/** The server's challenge on `ws`: the hello signed with the account's key; then the meeting the user
 * is in or asked for. */
async function signIn(ws: WebSocket, nonce: Uint8Array): Promise<void> {
  const key = state.key!;
  const sig = await key.sign(helloText(nonce));
  if (state.ws !== ws || ws.readyState !== WebSocket.OPEN) return;
  ws.send(state.codecs!.client.encode({ type: "hello", key: key.key, name: state.name, sig }));
  state.up = true;
  state.retry = RETRY;
  show("");
  const id = state.meeting?.id ?? (state.joining ? null : state.wanted);
  if (id !== null) send({ type: "meet", id });
  else if (state.creating) send({ type: "create", title: MEETING_TITLE, proof: -1 });
  changed();
}
